import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shipmentAPI } from '../../services/api';

const ShipmentForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    orderId: '',
    originPort: '',
    destinationPort: '',
    destinationAddress: '',
    containerType: '40HC',
    containerCount: 1,
    commodity: '',
    totalWeight: '',
    cargoReadyDate: '',
    requestedDeliveryDate: '',
    sellerEmail: '',
    buyerEmail: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Post-creation state
  const [createdShipment, setCreatedShipment] = useState(null);
  const [sendingQuotes, setSendingQuotes] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);
  const [quoteError, setQuoteError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePortChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Build payload, omitting empty optional fields
      const payload = {
        originPort: formData.originPort,
        destinationPort: formData.destinationPort,
        containerType: formData.containerType,
        containerCount: Number(formData.containerCount)
      };

      if (formData.orderId) payload.orderId = formData.orderId;
      if (formData.destinationAddress) payload.destinationAddress = formData.destinationAddress;
      if (formData.commodity) payload.commodity = formData.commodity;
      if (formData.totalWeight) payload.totalWeight = Number(formData.totalWeight);
      if (formData.cargoReadyDate) payload.cargoReadyDate = formData.cargoReadyDate;
      if (formData.requestedDeliveryDate) payload.requestedDeliveryDate = formData.requestedDeliveryDate;
      if (formData.sellerEmail) payload.sellerEmail = formData.sellerEmail;
      if (formData.buyerEmail) payload.buyerEmail = formData.buyerEmail;

      const result = await shipmentAPI.create(payload);
      setCreatedShipment(result.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create shipment');
      setSubmitting(false);
    }
  };

  const handleSendQuoteRequests = async () => {
    if (!createdShipment) return;
    setSendingQuotes(true);
    setQuoteError(null);

    try {
      const result = await shipmentAPI.requestQuotes(createdShipment.id);
      setQuoteResult(result.data);
    } catch (err) {
      setQuoteError(err.response?.data?.message || 'Failed to send quote requests');
    } finally {
      setSendingQuotes(false);
    }
  };

  // Post-creation success screen
  if (createdShipment) {
    return (
      <div className="shipment-form">
        <div className="page-header">
          <h1>Shipment Created</h1>
        </div>

        <div className="success-banner">
          <div className="success-banner-content">
            <h3>Shipment created successfully</h3>
            <p>
              <strong>{createdShipment.originPort}</strong> → <strong>{createdShipment.destinationPort}</strong>
              {' | '}
              {createdShipment.containerCount}x {createdShipment.containerType}
              {createdShipment.orderId && ` | Order: ${createdShipment.orderId}`}
            </p>
          </div>
        </div>

        {/* Quote request section */}
        {!quoteResult && (
          <div className="form-section">
            <h2>Send Quote Requests</h2>
            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Send email requests to matching freight providers. They will reply with pricing quotes.
            </p>
            {quoteError && <div className="error-message">{quoteError}</div>}
            <button
              className="btn btn-primary"
              onClick={handleSendQuoteRequests}
              disabled={sendingQuotes}
            >
              {sendingQuotes ? 'Sending...' : 'Send Quote Requests to Providers'}
            </button>
          </div>
        )}

        {/* Quote result */}
        {quoteResult && (
          <div className="success-banner" style={{ background: '#e8f5e9', borderColor: '#2e7d32' }}>
            <div className="success-banner-content">
              <h3>Quote requests sent</h3>
              <p>
                <strong>{quoteResult.providersContacted}</strong> provider{quoteResult.providersContacted !== 1 ? 's' : ''} contacted
                {' | '}
                Tracking: <strong>{quoteResult.trackingId}</strong>
              </p>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                Providers will reply by email. The system polls for responses every 5 minutes.
              </p>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button onClick={() => navigate('/shipments')} className="btn btn-secondary">
            Back to Shipments
          </button>
          <button onClick={() => navigate(`/shipments/${createdShipment.id}`)} className="btn btn-primary">
            View Shipment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shipment-form">
      <div className="page-header">
        <h1>New Shipment</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h2>Route</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="originPort">Origin Port *</label>
              <input
                type="text"
                id="originPort"
                name="originPort"
                value={formData.originPort}
                onChange={handlePortChange}
                required
                maxLength={5}
                placeholder="e.g. CNSHA"
              />
            </div>

            <div className="form-group">
              <label htmlFor="destinationPort">Destination Port *</label>
              <input
                type="text"
                id="destinationPort"
                name="destinationPort"
                value={formData.destinationPort}
                onChange={handlePortChange}
                required
                maxLength={5}
                placeholder="e.g. ESVLC"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="destinationAddress">Final Delivery Address</label>
            <input
              type="text"
              id="destinationAddress"
              name="destinationAddress"
              value={formData.destinationAddress}
              onChange={handleChange}
              maxLength={500}
              placeholder="Full delivery address (for trucking quotes)"
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Cargo</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="containerType">Container Type *</label>
              <select
                id="containerType"
                name="containerType"
                value={formData.containerType}
                onChange={handleChange}
                required
              >
                <option value="20GP">20GP - 20' General Purpose</option>
                <option value="40GP">40GP - 40' General Purpose</option>
                <option value="40HC">40HC - 40' High Cube</option>
                <option value="45HC">45HC - 45' High Cube</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="containerCount">Container Count *</label>
              <input
                type="number"
                id="containerCount"
                name="containerCount"
                value={formData.containerCount}
                onChange={handleChange}
                required
                min={1}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="commodity">Commodity</label>
              <input
                type="text"
                id="commodity"
                name="commodity"
                value={formData.commodity}
                onChange={handleChange}
                maxLength={200}
                placeholder="e.g. Ceramic tiles"
              />
            </div>

            <div className="form-group">
              <label htmlFor="totalWeight">Total Weight (kg)</label>
              <input
                type="number"
                id="totalWeight"
                name="totalWeight"
                value={formData.totalWeight}
                onChange={handleChange}
                min={0}
                step="0.01"
                placeholder="e.g. 18000"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Dates</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="cargoReadyDate">Cargo Ready Date</label>
              <input
                type="date"
                id="cargoReadyDate"
                name="cargoReadyDate"
                value={formData.cargoReadyDate}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="requestedDeliveryDate">Requested Delivery Date</label>
              <input
                type="date"
                id="requestedDeliveryDate"
                name="requestedDeliveryDate"
                value={formData.requestedDeliveryDate}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Reference & Stakeholders</h2>

          <div className="form-group">
            <label htmlFor="orderId">Order ID</label>
            <input
              type="text"
              id="orderId"
              name="orderId"
              value={formData.orderId}
              onChange={handleChange}
              maxLength={100}
              placeholder="External order reference"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sellerEmail">Seller Email</label>
              <input
                type="email"
                id="sellerEmail"
                name="sellerEmail"
                value={formData.sellerEmail}
                onChange={handleChange}
                placeholder="seller@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="buyerEmail">Buyer Email</label>
              <input
                type="email"
                id="buyerEmail"
                name="buyerEmail"
                value={formData.buyerEmail}
                onChange={handleChange}
                placeholder="buyer@example.com"
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/shipments')} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Shipment'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ShipmentForm;
